import React, { useState } from 'react';
import { Grid, TextField, MenuItem, Button, Typography } from '@mui/material';
import { USAbbreviations } from '../../../../utils/select_helpers/UnitedStatesAbbreviations';
import { useGooglePlacesAutocomplete } from '../../../../components/address_autocomplete/useGooglePlacesAutoComplete';
import CustomAddressAutocomplete from '../../../../components/address_autocomplete/CustomAddressAutocomplete';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';

export default function OrganizationForm({ initialData, onSubmit }) {
  const [formData, setFormData] = useState({
    companyName: initialData?.companyName || '',
  });

  const { 
    address, 
    city, 
    state, 
    zipCode: zip,
    addressPredictions, 
    handleAddressInputChange, 
    handleAddressSelect, 
    setAddress, 
    setCity, 
    setState, 
    setZipCode: setZip 
  } = useGooglePlacesAutocomplete(true);

  const [errors, setErrors] = useState({});
  const { t } = useTranslation();

  // Set initial values if they exist
  React.useEffect(() => {
    if (initialData) {
      setAddress(initialData.address || '');
      setCity(initialData.city || '');
      setState(initialData.state || '');
      setZip(initialData.zip || '');
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.companyName.trim()) {
      newErrors.companyName = t(StringKeys.COMPANY_NAME_IS_REQUIRED);
    }
    if (!address.trim()) {
      newErrors.address = t(StringKeys.ADDRESS_IS_REQUIRED);
    }
    if (!city.trim()) {
      newErrors.city = t(StringKeys.CITY_IS_REQUIRED);
    }
    if (!state) {
      newErrors.state = t(StringKeys.STATE_IS_REQUIRED);
    }
    if (!zip.trim()) {
      newErrors.zip = t(StringKeys.ZIP_CODE_IS_REQUIRED);
    } else if (!/^\d{5}(-\d{4})?$/.test(zip)) {
      newErrors.zip = t(StringKeys.PLEASE_ENTER_A_VALID_ZIP_CODE);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit({
        organizationDetails: {
          companyName: formData.companyName,
          address,
          city,
          state,
          zip
        }
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 1,
              color: 'text.secondary',
              fontSize: '0.925rem'
            }}
          >
            Company Information
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t(StringKeys.COMPANY_NAME)}
            value={formData.companyName}
            onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
            error={!!errors.companyName}
            helperText={errors.companyName}
            fullWidth
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f8fafc'
              }
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mt: 1,
              mb: 1,
              color: 'text.secondary',
              fontSize: '0.925rem'
            }}
          >
            Business Address
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <CustomAddressAutocomplete 
            value={address}
            onChange={handleAddressInputChange}
            onSelect={(selectedOption) => {
              if (selectedOption) {
                handleAddressSelect(null, selectedOption);
                
                setTimeout(() => {
                  document.activeElement?.blur();
                }, 100);
              }
            }}
            options={addressPredictions}
            label={t(StringKeys.ADDRESS)}
            required={true}
            error={!!errors.address}
            helperText={errors.address}
            fullWidth={true}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f8fafc'
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t(StringKeys.CITY)}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            error={!!errors.city}
            helperText={errors.city}
            fullWidth
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f8fafc'
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            select
            label={t(StringKeys.STATE)}
            value={state}
            onChange={(e) => setState(e.target.value)}
            error={!!errors.state}
            helperText={errors.state}
            fullWidth
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f8fafc'
              }
            }}
          >
            {USAbbreviations.map((abbr) => (
              <MenuItem key={abbr} value={abbr}>
                {abbr}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label={t(StringKeys.ZIP_CODE)}
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            error={!!errors.zip}
            helperText={errors.zip}
            fullWidth
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f8fafc'
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
        <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            sx={{ 
              textTransform: 'none', 
              fontWeight: '600',
              height: '56px',
              fontSize: '16px',
              borderRadius: '30px',
              boxShadow: '0 4px 12px rgba(57, 172, 231, 0.2)',
              mt: 2,
              '&:hover': {
                  boxShadow: '0 6px 16px rgba(57, 172, 231, 0.3)',
              },
              '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
              fullWidth
            >
            {t(StringKeys.NEXT)}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
} 